import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import axios from 'axios'
import QuestChat from '../components/QuestChat'
import FriendChat from '../components/FriendChat'
import ReviewCard from '../components/ReviewCard'
import './GamePage.css'

export default function GamePage({ user }) {
  const { gameId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const questChatRef = useRef(null)

  // Parse description: handle **bold** and [game links]
  const parseDescription = (text) => {
    if (!text) return null

    // First split by bold markers
    const boldParts = text.split(/\*\*([^*]+)\*\*/g)

    return boldParts.map((part, boldIndex) => {
      // Odd indices are bold text
      if (boldIndex % 2 === 1) {
        return <strong key={`bold-${boldIndex}`}>{part}</strong>
      }

      // For non-bold parts, handle game links
      const linkParts = part.split(/\[([^\]]+)\]/g)
      return linkParts.map((linkPart, linkIndex) => {
        // Odd indices are game names
        if (linkIndex % 2 === 1) {
          return (
            <span
              key={`link-${boldIndex}-${linkIndex}`}
              className="game-link"
              onClick={() => navigate(`/?search=${encodeURIComponent(linkPart)}`)}
            >
              {linkPart}
            </span>
          )
        }
        return linkPart
      })
    })
  }
  const [game, setGame] = useState(null)
  const [review, setReview] = useState(null)
  const [allReviews, setAllReviews] = useState([])
  const [similarGames, setSimilarGames] = useState([])
  const [activeTab, setActiveTab] = useState('quest') // Quest chat open by default
  const [loading, setLoading] = useState(true)
  const [isFavorite, setIsFavorite] = useState(false)
  const [refreshingRecs, setRefreshingRecs] = useState(false)
  const [loadingRecs, setLoadingRecs] = useState(false)
  const [questDescription, setQuestDescription] = useState(null)
  const [loadingDescription, setLoadingDescription] = useState(false)
  const [refreshingDescription, setRefreshingDescription] = useState(false)
  const [recsMessage, setRecsMessage] = useState(null)

  useEffect(() => {
    // Track page view for Quest context
    // Store previous game before loading new one
    const currentGame = sessionStorage.getItem('currentGame')
    if (currentGame && currentGame !== gameId) {
      sessionStorage.setItem('lastViewedGame', currentGame)
    }
    sessionStorage.setItem('currentGame', gameId)

    // Clear state when navigating to a new game
    setGame(null)
    setReview(null)
    setAllReviews([])
    setSimilarGames([])
    setQuestDescription('')
    setLoading(true)

    loadGameData()
    loadSimilarGames()
    loadQuestDescription()
  }, [gameId])

  // Scroll to Quest chat after review submission
  useEffect(() => {
    if (location.state?.scrollToQuest && questChatRef.current) {
      setTimeout(() => {
        questChatRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 500) // Wait for content to load
    }
  }, [location.state])

  const loadGameData = async () => {
    try {
      const [gameResponse, reviewResponse, allReviewsResponse, favoriteResponse] = await Promise.all([
        axios.get(`/api/games/${gameId}`),
        axios.get(`/api/reviews/game/${gameId}`, { withCredentials: true }),
        axios.get(`/api/reviews/game/${gameId}/all`, { withCredentials: true }),
        axios.get(`/api/favorites/check/${gameId}`, { withCredentials: true })
      ])

      setGame(gameResponse.data)
      setReview(reviewResponse.data)
      setAllReviews(allReviewsResponse.data || [])
      setIsFavorite(favoriteResponse.data.is_favorite)
    } catch (error) {
      console.error('Failed to load game data:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await axios.delete(`/api/favorites/${gameId}`, { withCredentials: true })
        setIsFavorite(false)
      } else {
        await axios.post(`/api/favorites/${gameId}`, {
          game_name: game.name,
          game_cover_url: game.background_image
        }, { withCredentials: true })
        setIsFavorite(true)
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`/api/reviews/${reviewId}`, { withCredentials: true })
      setReview(null) // Remove review from display
    } catch (error) {
      console.error('Failed to delete review:', error)
      alert('Failed to delete review. Please try again.')
    }
  }

  const loadSimilarGames = async () => {
    setLoadingRecs(true)
    console.log('[GamePage] Loading recommendations for game:', gameId)
    try {
      const response = await axios.get(`/api/games/${gameId}/quest-recommendations`)
      const data = response.data
      console.log('[GamePage] Received recommendations:', data)

      // Quest returns {message: "...", games: [...]}
      const games = data.games || []
      const message = data.message || null
      console.log('[GamePage] Setting similarGames state:', games.length, 'games')
      setSimilarGames(games)
      setRecsMessage(message)
    } catch (error) {
      console.error('[GamePage] Failed to load Quest recommendations:', error)
      setSimilarGames([])
      setRecsMessage('Failed to load recommendations')
    } finally {
      setLoadingRecs(false)
      console.log('[GamePage] Loading complete, loadingRecs=false')
    }
  }

  const refreshRecommendations = async () => {
    setRefreshingRecs(true)
    try {
      // Clear the cache
      await axios.delete(`/api/games/${gameId}/quest-recommendations`, { withCredentials: true })

      // Reload fresh recommendations (will trigger Quest to generate new ones)
      await loadSimilarGames()
    } catch (error) {
      console.error('Failed to refresh recommendations:', error)
      alert('Failed to get new suggestions. Please try again.')
    } finally {
      setRefreshingRecs(false)
    }
  }

  const loadQuestDescription = async () => {
    setLoadingDescription(true)
    try {
      const response = await axios.get(`/api/games/${gameId}/quest-description`, {
        withCredentials: true
      })
      if (response.data.description) {
        setQuestDescription(response.data.description)
      }
    } catch (error) {
      // Silently fail - will fall back to original description
      console.log('Quest description not available:', error.message)
    } finally {
      setLoadingDescription(false)
    }
  }

  const refreshQuestDescription = async () => {
    setRefreshingDescription(true)
    try {
      // Clear the cache
      await axios.delete(`/api/games/${gameId}/quest-description`, {
        withCredentials: true
      })

      // Reload fresh description
      await loadQuestDescription()
    } catch (error) {
      console.error('Failed to refresh description:', error)
      alert('Failed to get new description. Please try again.')
    } finally {
      setRefreshingDescription(false)
    }
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="game-loading">
            <div className="spinner"></div>
            <p>Loading game...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!game) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="game-not-found">
            <h2>Game not found</h2>
            <Link to="/" className="btn btn-primary">
              Go Home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="page-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className="container">
        <Link to="/" className="back-link">
          ← Back to Home
        </Link>

        <div className="game-header">
          {game.background_image && (
            <div className="game-cover">
              <img src={game.background_image} alt={game.name} />
            </div>
          )}
          <div className="game-info">
            <div className="game-title-row">
              <h1 className="game-title">{game.name}</h1>
              <motion.button
                className={`favorite-btn ${isFavorite ? 'favorited' : ''}`}
                onClick={toggleFavorite}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                {isFavorite ? '⭐' : '☆'}
              </motion.button>
            </div>
            <div className="game-meta">
              {game.released && (
                <span className="game-year">
                  📅 {new Date(game.released).getFullYear()}
                </span>
              )}
              {game.rating && (
                <span className="game-rating">⭐ {game.rating}/5</span>
              )}
            </div>
            {game.platforms && (
              <div className="game-platforms">
                {game.platforms.slice(0, 5).map((p) => (
                  <span key={p.platform.id} className="platform-tag">
                    {p.platform.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="game-actions">
          <motion.button
            className="btn btn-primary btn-large"
            onClick={() => navigate(`/review/${gameId}`)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            📝 Review It
          </motion.button>

          <motion.button
            className={`btn btn-large ${activeTab === 'quest' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab(activeTab === 'quest' ? null : 'quest')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            💬 Chat with Quest
          </motion.button>

          <motion.button
            className={`btn btn-large ${activeTab === 'friends' ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setActiveTab(activeTab === 'friends' ? null : 'friends')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            👥 Talk with Friends {user.friend_count > 0 && `(${user.friend_count})`}
          </motion.button>
        </div>

        {activeTab === 'quest' && game && (
          <motion.div
            ref={questChatRef}
            className="game-chat-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <QuestChat gameId={gameId} gameName={game.name} user={user} />
          </motion.div>
        )}

        {activeTab === 'friends' && game && (
          <motion.div
            className="game-chat-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FriendChat gameId={gameId} gameName={game.name} currentUser={user} />
          </motion.div>
        )}

        {review && (
          <div className="game-review-section">
            <h2 className="section-title">Your Review</h2>
            <ReviewCard
              review={review}
              currentUserId={user.user_id}
              onDelete={handleDeleteReview}
            />
            <Link to={`/review/${gameId}`} className="btn btn-secondary mt-md">
              Edit Review
            </Link>
          </div>
        )}

        {allReviews.length > 0 && (
          <div className="game-review-section">
            <h2 className="section-title">
              {allReviews.length === 1 ? 'Review' : 'Reviews'} ({allReviews.length})
            </h2>
            <div className="reviews-list">
              {allReviews.map((rev) => (
                <div key={rev.review_id} className={rev.is_ocean ? 'ocean-review-wrapper' : 'review-wrapper'}>
                  {rev.is_ocean && (
                    <div className="ocean-badge">
                      <span className="badge-icon">🌊</span>
                      <span className="badge-text">Ocean's Review</span>
                    </div>
                  )}
                  <ReviewCard
                    review={rev}
                    currentUserId={user.user_id}
                    onDelete={rev.user_id === user.user_id ? handleDeleteReview : null}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {(game.description_raw || questDescription) && (
          <div className="game-description">
            <div className="description-header">
              <h2 className="section-title">About This Game</h2>
              <motion.button
                className="btn btn-secondary btn-small"
                onClick={refreshQuestDescription}
                disabled={refreshingDescription || loadingDescription}
                whileHover={{ scale: refreshingDescription || loadingDescription ? 1 : 1.05 }}
                whileTap={{ scale: refreshingDescription || loadingDescription ? 1 : 0.95 }}
                title="Get Quest to rewrite this description just for you!"
              >
                {loadingDescription ? '✨ Loading...' : refreshingDescription ? '✨ Rewriting...' : '✨ Quest Rewrite'}
              </motion.button>
            </div>
            {loadingDescription ? (
              <div className="description-loading">
                <div className="spinner-small"></div>
                <span>Quest is writing a personalized description...</span>
              </div>
            ) : (
              <div className="description-text">
                {parseDescription(questDescription || game.description_raw)}
              </div>
            )}
            {questDescription && (
              <p className="description-note">
                <em>Personalized by Quest just for you!</em>
              </p>
            )}
          </div>
        )}

        {loadingRecs && (
          <div className="similar-games-section">
            <h2 className="section-title">Quest's Recommendations 🎮</h2>
            <div className="game-loading">
              <div className="spinner"></div>
              <p>Quest is picking games for you...</p>
            </div>
          </div>
        )}

        {!loadingRecs && similarGames.length === 0 && recsMessage && (
          <div className="similar-games-section">
            <div className="recommendations-header">
              <h2 className="section-title">Quest's Recommendations 🎮</h2>
              <motion.button
                className="btn btn-secondary"
                onClick={refreshRecommendations}
                disabled={refreshingRecs}
                whileHover={{ scale: refreshingRecs ? 1 : 1.05 }}
                whileTap={{ scale: refreshingRecs ? 1 : 0.95 }}
              >
                {refreshingRecs ? '🔄 Trying again...' : '🔄 Try Again'}
              </motion.button>
            </div>
            <p className="recommendations-error">{recsMessage}</p>
          </div>
        )}

        {!loadingRecs && similarGames.length > 0 && (
          <div className="similar-games-section">
            <div className="recommendations-header">
              <h2 className="section-title">Quest's Recommendations 🎮</h2>
              <motion.button
                className="btn btn-secondary"
                onClick={refreshRecommendations}
                disabled={refreshingRecs}
                whileHover={{ scale: refreshingRecs ? 1 : 1.05 }}
                whileTap={{ scale: refreshingRecs ? 1 : 0.95 }}
              >
                {refreshingRecs ? '🔄 Getting new suggestions...' : '🔄 Get New Suggestions'}
              </motion.button>
            </div>
            <p className="recommendations-intro">Quest picked these games just for you!</p>
            <div className="similar-games-grid">
              {similarGames.map((rec, index) => (
                <motion.div
                  key={index}
                  className="quest-rec-card"
                  whileHover={{ scale: 1.03, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => rec.rawg_id ? navigate(`/game/${rec.rawg_id}`) : navigate(`/?search=${encodeURIComponent(rec.title)}`)}
                >
                  {rec.image && (
                    <div className="quest-rec-image-container">
                      <img src={rec.image} alt={rec.title} className="quest-rec-image" />
                    </div>
                  )}
                  <div className="quest-rec-content">
                    <div className="quest-rec-header">
                      <h3 className="quest-rec-title">{rec.title}</h3>
                      <div className="quest-rec-badges">
                        <span className="badge badge-rating">{rec.ageRating}</span>
                        <span className="badge badge-difficulty">{rec.difficulty}</span>
                      </div>
                    </div>
                    <p className="quest-rec-reason">{rec.reason}</p>
                    <div className="quest-rec-platform">
                      <span className="platform-icon">🎮</span>
                      {rec.platform}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  )
}
